import { createConsumer, type Subscription } from "@rails/actioncable";

import type { WeeklyPlannerPayload } from "@/lib/api";

export type WeeklyPlannerPresenceUser = {
  source_client_id: string;
  user_id: number;
  email: string;
  name: string;
  active_cell?: {
    row_id: string;
    column_id: string;
  } | null;
};

type PlannerChangedEvent = {
  event_type: "planner_changed";
  week_start_on: string;
  actor_user_id: number;
  actor_user_email: string;
  source_client_id?: string;
  changed_scope?: Record<string, unknown>;
  planner: WeeklyPlannerPayload;
};

type PresenceSnapshotEvent = {
  event_type: "presence_snapshot";
  week_start_on: string;
  actor_user_id: number;
  source_client_id?: string;
  users: WeeklyPlannerPresenceUser[];
};

type WeeklyPlannerRealtimeEvent = PlannerChangedEvent | PresenceSnapshotEvent;

type ConnectionState = "connecting" | "connected" | "disconnected" | "rejected";

export type WeeklyPlannerRealtimeSubscription = {
  sendPresencePing: () => void;
  sendActiveCellChanged: (activeCell: { rowId: string; columnId: string } | null) => void;
  unsubscribe: () => void;
};

type SubscribeOptions = {
  weekStartOn: string;
  sourceClientId: string;
  onEvent: (event: WeeklyPlannerRealtimeEvent) => void;
  onConnectionState: (state: ConnectionState) => void;
  /** Called when the channel subscription is accepted (after connected). Start presence pings here. */
  onChannelConnected?: () => void;
};

let cableConsumer: ReturnType<typeof createConsumer> | null = null;

function getConsumer() {
  if (!cableConsumer) {
    cableConsumer = createConsumer("/cable");
  }
  return cableConsumer;
}

export function subscribeWeeklyPlannerRealtime(
  options: SubscribeOptions,
): WeeklyPlannerRealtimeSubscription {
  options.onConnectionState("connecting");

  let channelReady = false;

  const subscription = getConsumer().subscriptions.create(
    {
      channel: "WeeklyPlannerChannel",
      week_start_on: options.weekStartOn,
      source_client_id: options.sourceClientId,
    },
    {
      connected() {
        channelReady = true;
        options.onConnectionState("connected");
        options.onChannelConnected?.();
      },
      disconnected() {
        channelReady = false;
        options.onConnectionState("disconnected");
      },
      rejected() {
        channelReady = false;
        options.onConnectionState("rejected");
      },
      received(message: WeeklyPlannerRealtimeEvent) {
        options.onEvent(message);
      },
    },
  ) as Subscription;

  return {
    sendPresencePing: () => {
      if (!channelReady) return;
      subscription.perform("presence_ping", { source_client_id: options.sourceClientId });
    },
    sendActiveCellChanged: (activeCell) => {
      if (!channelReady) return;
      subscription.perform("active_cell_changed", {
        source_client_id: options.sourceClientId,
        active_cell: activeCell
          ? { row_id: activeCell.rowId, column_id: activeCell.columnId }
          : null,
      });
    },
    unsubscribe: () => {
      channelReady = false;
      subscription.unsubscribe();
    },
  };
}
